/* eslint no-underscore-dangle: ['error', { 'allow': ['_paymentsData',
'_coupon', '_showCouponInput', '_urlQuery'] }] */
/* global globals */
import get from 'lodash/get';
import assign from 'object-assign';
import { EventEmitter } from 'events';

const CHANGE_EVENT = 'change';
const CHANGE_ACTIVE_EVENT = 'changeActive';
const CHANGE_COUPON_EVENT = 'changeCoupon';

let _paymentsData = {
  type: 'subscription',
  title: '',
  active: null,
  terms: {},
  methods: [],
  popup: false,
  subscriptionMember: null,
  highlightMember: null,
};
let _coupon = {
  code: '',
  data: null,
  used: false,
  formOpen: false,
  error: false,
};
let _urlQuery = '';

const PaymentsStore = assign({}, EventEmitter.prototype, {
  emitChange() {
    this.emit(CHANGE_EVENT);
  },

  /**
   * Active/selected payment term has been changed
   */
  emitActiveChange() {
    this.emit(CHANGE_ACTIVE_EVENT);
  },

  /**
   * Follow active/selected term changes
   *
   * @param {function} callback
   */
  addActiveChangeListener(callback) {
    if (this.listenerCount(CHANGE_ACTIVE_EVENT)) {
      this.removeAllListeners(CHANGE_ACTIVE_EVENT);
    }
    this.on(CHANGE_ACTIVE_EVENT, callback);
  },

  /**
   * Some of coupon data has been changed
   */
  emitCouponChange() {
    this.emit(CHANGE_COUPON_EVENT);
  },

  /**
   * Follow coupon data changes
   *
   * @param {function} callback
   */
  addCouponChangeListener(callback) {
    if (this.listenerCount(CHANGE_COUPON_EVENT)) {
      this.removeAllListeners(CHANGE_COUPON_EVENT);
    }
    this.on(CHANGE_COUPON_EVENT, callback);
  },

  /**
   * Set data from 'globals'
   */
  setInitialData() {
    _paymentsData = get(globals, 'payments', {});
    _coupon = get(globals, 'coupon', {});
    _coupon.formOpen = this.getCouponCode().length;
    _coupon.error = _coupon.formOpen && !this.getCouponData();

    PaymentsStore.emitChange();
  },

  /**
   * Get type (expected - 'subscription', 'highlight' or 'subscription_other')
   */
  getType() {
    return get(_paymentsData, 'type', 'subscription');
  },

  /**
   * Get title (main payments view title, above subscription terms)
   */
  getTitle() {
    return get(_paymentsData, 'title', '');
  },

  /**
   * Get active/selected term length (for example - '30', '31', '92', ...)
   */
  getActive() {
    return get(_paymentsData, 'active', null);
  },

  /**
   * Set active/selected term length, if it's valid (is set in '_paymentsData.terms')
   *
   * @param {string} length
   */
  setActive(length) {
    if (!this.getTerms()[length]) {
      return;
    }

    _paymentsData.active = length;
    PaymentsStore.emitActiveChange();

    //
    if (!this.getCouponData() && this.getCouponCode().length) {
      _coupon.code = '';
      _coupon.formOpen = false;
      _coupon.error = false;
      PaymentsStore.emitCouponChange();
    }
  },

  /**
   * Get terms data
   */
  getTerms() {
    return get(_paymentsData, 'terms', {});
  },

  /**
   * Get payment methods
   */
  getMethods() {
    return get(_paymentsData, 'methods', []);
  },

  /**
   * Get target popup name (or false)
   */
  getPopup() {
    return get(_paymentsData, 'popup', false);
  },

  /**
   * Get 'subscription_other' target member
   */
  getSubscriptionMember() {
    return get(_paymentsData, 'subscriptionMember', null);
  },

  /**
   * Get 'highlight' target member
   */
  getHighlightMember() {
    return get(_paymentsData, 'highlightMember', null);
  },

  /**
   * Get all coupon data
   */
  getCoupon() {
    return _coupon;
  },

  /**
   * Get coupon data
   */
  getCouponData() {
    return get(_coupon, 'data', null);
  },

  /**
   * Set coupon data
   *
   * @param {object/null} data
   */
  setCouponData(data) {
    _coupon.data = data;
    PaymentsStore.emitCouponChange();
  },

  /**
   * Get coupon code
   */
  getCouponCode() {
    return get(_coupon, 'code', '');
  },

  /**
   * Set coupon code
   *
   * @param {string} value
   */
  setCouponCode(value) {
    _coupon.code = value;
    PaymentsStore.emitCouponChange();
  },

  /**
   * Get coupon used
   */
  getCouponUsed() {
    return get(_coupon, 'used', false);
  },

  /**
   * Get coupon form open
   */
  getCouponFormOpen() {
    return get(_coupon, 'formOpen', false);
  },

  /**
   * Set coupon form open
   *
   * @param {bool} value
   */
  setCouponFormOpen(value) {
    _coupon.formOpen = value;
    PaymentsStore.emitCouponChange();
  },

  /**
   * Get coupon error (true/false)
   */
  getCouponError() {
    return get(_coupon, 'error', false);
  },

  /**
   * Set coupon error
   *
   * @param {bool} value
   */
  setCouponError(value) {
    _coupon.error = value;
    PaymentsStore.emitCouponChange();
  },

  /**
   * Update url query
   */
  updateUrlQuery() {
    // Need update only for these types
    const type = this.getType();
    if (type !== 'subscription' && type !== 'subscription_other') {
      return;
    }

    // Url base
    _urlQuery = `${get(globals, 'webroot', '/')}payments/${type}`;

    // Add selected term length if set and not '1' (which only indicates that popup should be shown)
    const active = this.getActive();
    if (active && active !== '1') {
      _urlQuery += `/${active}`;
    }

    // If 'subscription_other' also add target member id
    if (type === 'subscription_other') {
      const memberId = get(_paymentsData, 'subscriptionMember._id', null);
      if (memberId) {
        _urlQuery += `?target=${memberId}`;
      }
    }

    // Add coupon code if set
    const couponCode = this.getCouponCode();
    if (couponCode.length) {
      _urlQuery += _urlQuery.includes('?') ? `&coupon_code=${couponCode}` : (`?coupon_code=${couponCode}`);
    }
  },

  /**
   * Get url query
   *
   * @param {bool} includeCouponCode
   * Default - true. If - false, will return query without 'coupon_code' parameter.
   * Necessary for canceling coupon usage.
   *
   */
  getUrlQuery(includeCouponCode = true) {
    if (includeCouponCode || !_urlQuery.includes('coupon_code')) {
      return _urlQuery;
    }

    // Removes 'coupon_code' and 'target' if they are set
    let targetUrl = _urlQuery.split('?')[0];
    // Add only target member id if 'subscription_other'
    if (this.getType() === 'subscription_other') {
      const memberId = get(_paymentsData, 'subscriptionMember._id', null);
      if (memberId) {
        targetUrl += `?target=${memberId}`;
      }
    }

    //
    return targetUrl;
  },

  /**
   * Create and return payment method url
   *
   * @param {string} adapter
   * @param {string} method paysera payment method
   */
  getPaymentMethodUrl(adapter, method = '') {
    // If no term selected, and for some reason payment options are visible,
    // just return page url, to refresh page if payment option clicked
    const active = this.getActive();
    if (!active || active === '1') {
      return _urlQuery;
    }

    //
    const type = this.getType();

    // Url base
    let paymentUrl = `${get(globals, 'webroot', '/')}payments/${adapter}/${type}/${active}`;

    // Paysera method
    if (adapter === 'paysera' && method) {
      paymentUrl += `/${method}`;
    }

    // If 'subscription_other' also add target member id
    if (type === 'subscription_other') {
      const memberId = get(_paymentsData, 'subscriptionMember._id', null);
      if (memberId) {
        paymentUrl += `?target=${memberId}`;
      }
    }

    // Add coupon code if coupon activated
    const couponData = this.getCouponData();
    if (couponData && couponData.code.length) {
      const { code } = couponData;
      paymentUrl += paymentUrl.includes('?') ? `&coupon_code=${code}` : (`?coupon_code=${code}`);
    }

    return paymentUrl;
  },

});

module.exports = PaymentsStore;
